<template>
  <div>
    <ul class="health-checks" data-testid="health-check-list">
      <DcaBanner />
      <PreDefaultNoticeBanner />
      <InvoluntaryPaygSwitchedBanner />
      <InvoluntaryPaygBanner />
      <div
        v-if="!filteredHealthChecks.length && !current.healthChecks.isFetching"
        class="pa-4 text-medium-emphasis"
      >
        <template v-if="filter === 'failed'">
          <i>No checks to resolve &#10003;</i>
        </template>
        <template v-else-if="filter === 'passed'">
          <i>No checks passed &#10008;</i>
        </template>
      </div>
      <li
        v-for="(item, itemIndex) in filteredHealthChecks"
        v-else
        :key="`healthcheckitem-${itemIndex}`"
        class="health-checks__item d-flex align-center py-3 px-5"
      >
        <div class="d-flex flex-wrap align-center w-100">
          <div
            class="health-checks__item-title text-truncate"
            :title="item.title"
          >
            {{ item.title }}
          </div>
          <div v-if="item.isCollapsable" class="w-100 pt-2">
            <div
              v-for="(check, checkIndex) in item.checks"
              :key="checkIndex"
              class="d-flex align-center py-1"
            >
              <div
                v-if="check.title"
                :key="`checktitle${checkIndex}`"
                class="health-checks__item-title text-truncate"
                :title="check.title"
              >
                {{ check.title }}
              </div>

              <div class="px-3">
                <SoStatus
                  :key="`healthcheck-${checkIndex}`"
                  :status="check.status"
                />
              </div>
            </div>
          </div>
          <div v-else class="px-3 d-flex flex-column gap-1">
            <SoStatus
              v-for="(check, checkIndex) in item.checks"
              :key="`healthcheck-${checkIndex}`"
              :status="check.status"
            />
          </div>
        </div>
        <div class="ml-auto">
          <SoButton
            v-if="item.passedCheckButton && !hasFailedCheck(item.checks)"
            v-bind="item.passedCheckButton"
          ></SoButton>
          <SoButton
            v-if="item.failedCheckButton && hasFailedCheck(item.checks)"
            v-bind="item.failedCheckButton"
            :data-test-id="`button-${item.failedCheckButton.title
              ?.toLowerCase()
              .replace(/\s/g, '-')}`"
          ></SoButton>
        </div>
      </li>
    </ul>
    <UnderReviewPeriodsModal v-model="openReviewPeriodsModal" />
    <SubmitReadingsModal v-model="openSubmitReadingModal" />
    <ImbpRegisterInterestModal v-model="openRegisterInterestModal" />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { ComponentProps } from "@/types/helpers"
  import type { PropType } from "vue"
  import SubmitReadingsModal from "@/views/customer/meterpoints/modals/SubmitReadingsModal.vue"
  import UnderReviewPeriodsModal from "../modals/UnderReviewPeriodsModal.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import {
    HealthCheckInfo,
    IAccountHealthOverview,
  } from "@/services-handlers/getAccountHealthChecks"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { RouteNames } from "@/types/router"
  import PreDefaultNoticeBanner from "./PreDefaultNoticeBanner.vue"
  import ImbpRegisterInterestModal from "@/views/customer/imbp/components/ImbpRegisterInterestModal.vue"
  import { errorReporter } from "@/services/ErrorService"
  import DcaBanner from "./DcaBanner.vue"
  import InvoluntaryPaygBanner from "./InvoluntaryPaygBanner.vue"
  import InvoluntaryPaygSwitchedBanner from "./InvoluntaryPaygSwitchedBanner.vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import { MeterpointService } from "@/services/MeterpointService"

  type HealthCheckItem = {
    title: string
    checks: HealthCheckInfo[]
    isCollapsable?: boolean
    passedCheckButton?: ComponentProps<typeof SoButton>
    failedCheckButton?: ComponentProps<typeof SoButton>
  }

  export type HealthCheckFilter = "all" | "failed" | "passed"

  export default defineComponent({
    components: {
      UnderReviewPeriodsModal,
      SubmitReadingsModal,
      SoButton,
      SoStatus,
      PreDefaultNoticeBanner,
      ImbpRegisterInterestModal,
      DcaBanner,
      InvoluntaryPaygBanner,
      InvoluntaryPaygSwitchedBanner,
    },
    props: {
      healthChecks: {
        type: Object as PropType<IAccountHealthOverview>,
        required: true,
      },
      filter: {
        type: String as PropType<HealthCheckFilter>,
        default: "all",
      },
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        openReviewPeriodsModal: false,
        openSubmitReadingModal: false,
        openRegisterInterestModal: false,
      }
    },
    computed: {
      isEnergyUsageEnabled(): boolean {
        return FeaturesService.isFeatureActive("ENERGY_USAGE_NOVA") ?? false
      },
      hasSmartMeter(): boolean {
        const meterpoint = MeterpointService.getForAccount(
          this.current.accountNumber,
        )

        return meterpoint.data.some((meterpoint) => meterpoint.hasSmartMeter)
      },
      filteredHealthChecks(): HealthCheckItem[] {
        const healthChecks = this.healthCheckItems.filter(
          (item) => !!item.checks.length,
        )

        if (this.filter === "failed")
          return healthChecks.filter(
            (hc) => this.hasFailedCheck(hc.checks) && hc.checks.length > 0,
          )

        if (this.filter === "passed")
          return healthChecks.filter(
            (hc) => !this.hasFailedCheck(hc.checks) && hc.checks.length > 0,
          )

        return healthChecks
      },
      healthCheckItems(): HealthCheckItem[] {
        return [
          {
            title: "Default Status",
            checks: this.healthChecks.defaultStatus,
            failedCheckButton: {
              title: "Financials",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerFinancials,
                }),
            },
          },
          {
            title: "Meter Readings",
            checks: this.healthChecks.meterReadings,
            isCollapsable: true,
            failedCheckButton: {
              title: "Submit Readings",
              onClick: () => (this.openSubmitReadingModal = true),
            },
          },
          {
            title: "Direct Debit Mandate",
            checks: this.healthChecks.directDebitMandates,
            failedCheckButton: {
              title: "Set up DD",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerFinancials,
                }),
            },
          },
          {
            title: "Active Payment Schedule",
            checks: this.healthChecks.paymentSchedules,
            failedCheckButton: {
              title: "Payments",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerReviewPaymentAmount,
                }),
            },
          },
          {
            title: "Failed Payments Within 60 Days",
            checks: this.healthChecks.failedPayments,
            failedCheckButton: {
              title: "Financials",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerFinancialsTransactions,
                }),
            },
          },
          {
            title: "Has Open Complaint",
            checks: this.healthChecks.hasOpenComplaint,
            failedCheckButton: {
              title: "Complaints",
              onClick: () =>
                this.$router.push({ name: RouteNames.CustomerComplaints }),
            },
          },
          {
            title: "Within Renewal Period",
            checks: this.healthChecks.withinRenewalPeriod,
            failedCheckButton: {
              title: "Renewals",
              onClick: () =>
                this.$router.push({ name: RouteNames.CustomerRenewals }),
            },
          },
          {
            title: "Failed Bills",
            checks: this.healthChecks.failedBills,
            failedCheckButton: {
              title: "Check Failed Bill",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerFinancialsTransactions,
                }),
            },
          },
          {
            title: "Under Review Periods",
            checks: this.healthChecks.underReviewPeriods,
            failedCheckButton: {
              title: "View & Edit Reviews",
              onClick: () => (this.openReviewPeriodsModal = true),
            },
            passedCheckButton: {
              title: "Add review",
              type: "text",
              prependIcon: "plus",
              onClick: () => (this.openReviewPeriodsModal = true),
            },
          },
          {
            title: "Open Junifer Tickets",
            checks: this.healthChecks.openJuniferTickets,
            failedCheckButton: {
              title: "Tickets",
              onClick: () =>
                this.$router.push({ name: RouteNames.CustomerJunifer }),
            },
          },
          {
            title: "RTS meters stop working in June",
            checks: this.healthChecks.rtsMeters,
            failedCheckButton: this.computeRtsButton(),
          },
          {
            title: "Smart meter issue",
            checks: this.healthChecks.smartMetersEligibleForExchange,
            failedCheckButton: this.computeSmartMetersEligibleButton(),
          },
          {
            title: "Outbound Photo Request",
            checks: this.healthChecks.openOutboundRequest,
            failedCheckButton: {
              title: "View Request",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerMeterpoints,
                  query: { openPhotoHistoryModal: "true" },
                }),
            },
          },
          {
            title: "Security deposit requested",
            checks: this.healthChecks.hasSecurityDepositRequest,
            failedCheckButton: {
              title: "View Request",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.AdminCreditChecks,
                }),
            },
          },
          {
            title: "Energy Usage Data",
            checks:
              this.isEnergyUsageEnabled && this.hasSmartMeter
                ? this.healthChecks.energyUsage
                : [],
            isCollapsable: true,
            failedCheckButton: {
              title: "View Meterpoints",
              onClick: () =>
                this.$router.push({
                  name: RouteNames.CustomerMeterpoints,
                }),
            },
          },
        ]
      },
    },
    methods: {
      hasFailedCheck(checks: HealthCheckInfo[]): boolean {
        return checks.some(
          (check) =>
            check.status.type === "critical" || check.status.type === "caution",
        )
      },
      async handleRtsFromHealthCheck() {
        try {
          const hasSlots =
            this.current.imbpAvailability.data?.hasAvailableSlots ?? false

          if (hasSlots) {
            this.$router.push({
              name: RouteNames.CustomerImbp,
            })
          } else {
            this.openRegisterInterestModal = true
          }
        } catch (error) {
          errorReporter.report(error)
          this.openRegisterInterestModal = true
        }
      },

      computeSmartMetersEligibleButton() {
        const isBooked = this.healthChecks.smartMetersEligibleForExchange.some(
          (check) => check.status.type === "caution",
        )
        return {
          title: isBooked ? "Manage appointment" : "Book Smart Meter Exchange",
          onClick: () => this.$router.push({ name: RouteNames.CustomerImbp }),
        }
      },

      computeRtsButton() {
        const isBooked = this.healthChecks.rtsMeters.some(
          (check) => check.status.type === "caution",
        )

        if (isBooked) {
          return {
            title: "Manage Appointment",
            onClick: () => {
              this.$router.push({ name: RouteNames.CustomerImbp })
            },
          }
        }

        return {
          title: "Book Smart Meter Install",

          onClick: () => this.handleRtsFromHealthCheck(),
        }
      },
    },
  })
</script>

<style scoped>
  .health-checks__item:not(:first-child) {
    border-top: 1px solid rgb(var(--v-theme-border));
  }

  .health-checks__item-title {
    flex-shrink: 0;
    width: 230px;
  }
</style>
